import React, { useMemo } from "react";
import ButtonLoading from "../../Button/ButtonLoading";
import { Box, Checkbox, Grid, ListItemText, Typography } from "@mui/material";
import { useCommunicationMedium } from "./CommunicationMediumProvider";
import { getBeaSafeId, getSubject, isValidBeaMediaObject } from "./communicationHelperFunctions";
import SendForm from "./SendForm";
import ValidatorTextField from "../../Validator/ValidatorTextField";
import { formValue } from "../../../services/formServiceFunctions";
import { beaSafeIdFieldDefault, requiredFieldDefault } from "../../../services/validationRules";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import _ from "lodash";
import MenuItem from "@mui/material/MenuItem";
import useForm from "../../../hooks/useForm";
import ApiClient from "../../../services/ApiClient";
import { useSnackbar } from "notistack";
import { MediaObject } from "../../../types/MediaObject";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { createPortal } from "react-dom";
import { UseForm } from "../../../types/UseForm";
import { useDrawer } from "../../../provider/DrawerProvider";
import useDraftAutosave from "../../../hooks/useDraftAutosave";
import useEffectWithoutMount from "../../../hooks/useEffectWithoutMount";
import DraftAutosaveIndicator from "../../GlobalDrawer/DraftAutosaveIndicator";

export type BeaSendProps = {
  beaDraft: {
    beaSafeId: string;
    externalReference: string;
    attachments: Array<number>;
    legalDocuments: Array<number>;
    subject: string;
    priority: boolean;
  } | null;
  overwrittenHandleSave?: Function;
};

export default function BeaSend({ beaDraft, overwrittenHandleSave }: BeaSendProps) {
  const { product, handleClose, recipientType, externalReference, externalReferenceIsEditable } =
    useCommunicationMedium();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { drawerActionsNode, drawerHeaderNode } = useDrawer();

  let mediaObjectsFilter = {
    product: product.productClassName,
    productId: product.id,
  };
  const { data: mediaObjectsCollection } = useQuery({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilter),
    queryFn: () => fetchCollection("media_objects", mediaObjectsFilter),
    enabled: !!product
  });

  const mediaObjects = _.get(mediaObjectsCollection, "hydra:member");
  const { enqueueSnackbar } = useSnackbar();

  const beaSafeId = useMemo(() => {
    return getBeaSafeId(product, recipientType);
  }, [product, recipientType]);

  const buildBeaDraftData = (currentValues: UseForm["values"]) => ({
    communicationMedium: "bea",
    recipientType: recipientType,
    bea: {
      beaSafeId: currentValues.beaSafeId,
      externalReference: currentValues.externalReference,
      attachments: currentValues.attachments,
      legalDocuments: currentValues.legalDocuments,
      subject: currentValues.subject,
      priority: currentValues.priority === "true",
    },
  });

  const onSubmit = async ({ values }: { values: UseForm["values"] }) => {
    //draft mode must never send or mutate anything; persistence runs through the autosave (see FaxSend for the discussion)
    if (overwrittenHandleSave) {
      return;
    }
    if (recipientType === "opponentLawyer" && !product.opponentLawFirm.organization.beaSafeId) {
      await ApiClient.patch(product.opponentLawFirm["@id"], {
        body: JSON.stringify({ beaSafeId: values.beaSafeId }),
      });
    }

    try {
      await ApiClient.post("bea/send", {
        body: JSON.stringify({
          beaSafeId: values.beaSafeId,
          referenceSender: product.reference,
          referenceRecipient: values.externalReference,
          subject: values.subject,
          backofficeCase: product?.backofficeCase?.id,
          createdBy: currentUser["@id"],
          legalDocuments: values.legalDocuments,
          attachments: values.attachments,
          forceSend: true,
          priority: values.priority === "true",
        }),
      });
      enqueueSnackbar("beA-Nachricht wurde erfolgreich versendet", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } catch (e: any) {
      const response = _.get(e, "response");
      const errorMessage =
        response?.errorMessage ||
        "Die beA-Nachricht konnte nicht versendet werden. Bitte Fristen prüfen und ggf. per Fax versenden. Erneuter Versand wird von System stündlich neu versucht";
      const additionalMessages = response?.additionalMessages || [];

      enqueueSnackbar(
        <Box sx={{
          textAlign: "left"
        }}>
          <Typography variant={"h5"}>{errorMessage}</Typography>
          {additionalMessages.length > 0 && (
            <ul>
              {_.map(additionalMessages, (additionalMessage, index) => (
                <li key={index}>{additionalMessage}</li>
              ))}
            </ul>
          )}
        </Box>,
        {
          variant: "error",
          isNonInteractive: true,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          persist: true,
        }
      );
    }
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("bea_messages", { case: product.backofficeCase.id })
    });
    handleClose();
  };

  const initialValues = {
    beaSafeId: beaDraft?.beaSafeId || beaSafeId || "",
    externalReference: beaDraft ? beaDraft.externalReference : (externalReference || ""),
    attachments: beaDraft?.attachments ?? [],
    legalDocuments: beaDraft?.legalDocuments ?? [],
    subject: beaDraft?.subject ?? getSubject(product),
    priority: beaDraft?.priority ? "true" : "false",
  };

  const { values, handleChange, handleBlur, handleSubmit, isLoading, registerValidators, errors } = useForm({
    initialValues,
    onSubmit,
    identifier: "" + product.id,
    clearFormAfterSubmit: true,
    disableValidation: !!overwrittenHandleSave,
  });

  const { saveState, scheduleSave } = useDraftAutosave({
    enabled: !!overwrittenHandleSave,
    persist: async () => {
      await overwrittenHandleSave?.(buildBeaDraftData(values));
    },
  });

  useEffectWithoutMount(() => scheduleSave(), [values, recipientType]);

  // These effects must not run on mount: the initial values may come from an
  // AI-edited execution step payload, and overwriting them here would trigger
  // the draft autosave and clobber the AI edit with derived/stale values.
  useEffectWithoutMount(() => {
    handleChange({
      target: {
        name: "beaSafeId",
        value: beaSafeId,
      },
    });
  }, [beaSafeId]);

  useEffectWithoutMount(() => {
    handleChange({
      target: {
        name: "externalReference",
        value: externalReference,
      },
    });
  }, [externalReference]);

  useEffectWithoutMount(() => {
    handleChange({
      target: {
        name: "priority",
        value: "false",
      },
    });
  }, [recipientType]);

  if (!drawerActionsNode) {
    return null;
  }

  const possibleAttachments = _.filter(mediaObjects, (mediaObject) => {
    return !_.find(_.get(values, "legalDocuments"), (legalDocument) => legalDocument === mediaObject.id);
  });

  const possibleLegalDocuments = _.filter(mediaObjects, (mediaObject) => {
    return !_.find(_.get(values, "attachments"), (attachment) => attachment === mediaObject.id);
  });

  const getDescriptionFromMediaobjectId = (id: number) => {
    return _.find(mediaObjects, (mediaObject) => mediaObject.id === id).description;
  };

  return (
    <>
      <Grid container>
        <Grid size={12}>
          <SendForm>
            {beaSafeId === "" && (
              <Grid size={12}>
                <ValidatorTextField
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name={"beaSafeId"}
                  value={formValue(values, "beaSafeId")}
                  label={"beA-Adresse Empfänger"}
                  registerValidators={registerValidators}
                  validators={[...requiredFieldDefault, ...beaSafeIdFieldDefault]}
                  error={!!_.get(errors, "beaSafeId")}
                  helperText={_.get(errors, "beaSafeId")}
                  isHighlighted={false}
                  initialDependentValidationFields={[]}
                />
              </Grid>
            )}
            <Grid size={12}>
              <ValidatorTextField
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={!externalReferenceIsEditable}
                name={"externalReference"}
                value={formValue(values, "externalReference")}
                label={"Aktenzeichen Empfänger"}
                isHighlighted={false}
                registerValidators={() => {}}
                validators={[]}
                initialDependentValidationFields={[]}
              />
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                onChange={handleChange}
                onBlur={handleBlur}
                name={"subject"}
                value={formValue(values, "subject")}
                label={"Betreff"}
                registerValidators={registerValidators}
                validators={[...requiredFieldDefault]}
                error={!!_.get(errors, "subject")}
                helperText={_.get(errors, "subject")}
                isHighlighted={false}
                initialDependentValidationFields={[]}
              />
            </Grid>
            <Grid size={12}>
              <ValidatorSelect
                label={"Schriftsatz"}
                name={"legalDocuments"}
                value={formValue(values, "legalDocuments")}
                onChange={handleChange}
                onBlur={handleBlur}
                registerValidators={registerValidators}
                validators={requiredFieldDefault}
                error={!!_.get(errors, "legalDocuments")}
                helperText={_.get(errors, "legalDocuments")}
                multiple
                renderValue={(selected: Array<number>) =>
                  _.map(selected, (mediaObjectId) => getDescriptionFromMediaobjectId(mediaObjectId)).join(", ")
                }
              >
                {_.map(possibleLegalDocuments, (mediaObject: MediaObject) => (
                  <MenuItem key={mediaObject.id} value={mediaObject.id} disabled={!isValidBeaMediaObject(mediaObject)}>
                    <Checkbox
                      checked={
                        !!_.find(
                          formValue(values, "legalDocuments"),
                          (selectedMediaObject) => selectedMediaObject === mediaObject.id
                        )
                      }
                    />
                    <ListItemText primary={mediaObject.description} />
                  </MenuItem>
                ))}
              </ValidatorSelect>
            </Grid>
            <Grid size={12}>
              <ValidatorSelect
                label={"Anhang"}
                name={"attachments"}
                value={formValue(values, "attachments")}
                onChange={handleChange}
                onBlur={handleBlur}
                multiple
                renderValue={(selected: Array<number>) =>
                  _.map(selected, (mediaObjectId) => getDescriptionFromMediaobjectId(mediaObjectId)).join(", ")
                }
              >
                {_.map(possibleAttachments, (mediaObject: MediaObject) => (
                  <MenuItem key={mediaObject.id} value={mediaObject.id} disabled={!isValidBeaMediaObject(mediaObject)}>
                    <Checkbox
                      checked={
                        !!_.find(
                          formValue(values, "attachments"),
                          (selectedMediaObject) => selectedMediaObject === mediaObject.id
                        )
                      }
                    />
                    <ListItemText primary={mediaObject.description} />
                  </MenuItem>
                ))}
              </ValidatorSelect>
            </Grid>
          </SendForm>
        </Grid>
        {["court", "authority"].includes(recipientType) && (
          <Grid size={12}>
            <ValidatorSelect
              label={"Sendungspriorität"}
              name={"priority"}
              value={formValue(values, "priority")}
              onChange={handleChange}
            >
              <MenuItem value={"false"}>Keine Angabe</MenuItem>
              <MenuItem value={"true"}>Eilt</MenuItem>
            </ValidatorSelect>
          </Grid>
        )}
      </Grid>
      {overwrittenHandleSave
        ? drawerHeaderNode && createPortal(<DraftAutosaveIndicator saveState={saveState} />, drawerHeaderNode)
        : createPortal(
            <Grid container sx={{ width: "100%" }}>
              <Grid size={3} sx={{ display: { xs: "none", md: "block" } }} />
              <Grid size={{ xs: 12, md: 6 }} sx={{ marginTop: 6 }}>
                <ButtonLoading type={"submit"} variant={"contained"} onClick={handleSubmit} isLoading={isLoading}>
                  Nachricht Versenden
                </ButtonLoading>
              </Grid>
            </Grid>,
            drawerActionsNode
          )}
    </>
  );
}
